import { EmailInput, PasswordInput } from '@designsforhealth/dfh-react-components'
import { graphql } from 'gatsby'
import React, { useMemo } from 'react'
import styled, {
  css,
  DefaultTheme,
  InterpolationFunction,
  ThemedStyledProps,
} from 'styled-components'

import useSiteSettings from '../../../../hooks/useSiteSettings'
import { useAuth } from '../../../../lib/auth'
import Spacer from '../../../Spacer'
import { HelpCenterMenuEntry, HelpCenterMenuEntryValid } from '../help-center/types'
import ChangePassword from './ChangePassword'

// adapted from https://stackoverflow.com/questions/16699007/regular-expression-to-match-standard-10-digit-phone-number/16702965#16702965
const telephoneRegex = /^\s*(?:\+?\d{1,3})?[-. (]*\d{3}[-. )]*\d{3}[-. ]*\d{4}(?: *x\d+)?\s*$/

function isSimpleTelephoneMenuEntry(
  value: HelpCenterMenuEntry | null | undefined,
): value is HelpCenterMenuEntryValid {
  if (!value) {
    return false
  }
  const { link, linkText } = value
  return !!link?.href && !!linkText && link.href.startsWith('tel:') && telephoneRegex.test(linkText)
}

const StyledPhoneNumber = styled.a`
  color: ${({ theme }) => theme.colors.brand.orange};
  white-space: nowrap;
`

const inputStyles: InterpolationFunction<
  ThemedStyledProps<{ disabled?: boolean }, DefaultTheme>
> = ({ disabled, theme }) =>
  disabled
    ? css`
        label,
        input {
          // use cool600 instead of cool500 to increase contrast
          color: ${theme.colors.coolGray.cool600};
        }
      `
    : css``

const StyledEmailInput = styled(EmailInput)(inputStyles)

const StyledPasswordInput = styled(PasswordInput)(inputStyles)

const PasswordSettings: React.FC = () => {
  const { user } = useAuth()

  const { helpCenterMenu: helpCenterMenuEntries } = useSiteSettings() || {}
  const phoneNumberMenuEntry = useMemo(
    () => helpCenterMenuEntries?.find(isSimpleTelephoneMenuEntry),
    [helpCenterMenuEntries],
  )

  return (
    <>
      <h3>Email & Password</h3>
      <Spacer size={3} />
      <StyledEmailInput
        label="Email Address"
        name="email"
        defaultValue={user?.email}
        readOnly
        disabled
      />
      <div>
        Contact Customer Experience{' '}
        {!!phoneNumberMenuEntry && (
          <>
            at{' '}
            <StyledPhoneNumber href={phoneNumberMenuEntry.link.href}>
              {phoneNumberMenuEntry.linkText}
            </StyledPhoneNumber>{' '}
          </>
        )}
        to change your email.
      </div>
      <br />
      <StyledPasswordInput
        label="Password"
        name="password"
        defaultValue="********"
        readOnly
        disabled
      />
      <ChangePassword />
    </>
  )
}

export default PasswordSettings

export const helpCenterMenu = graphql`
  fragment helpCenterMenuFragment on SanitySiteSettings {
    helpCenterMenu {
      _key
      linkText
      label
      link {
        _type
        href
        blank
      }
    }
  }
`
